import React, { useRef, useCallback, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useReport } from '@/features/cars/hooks/useReports';
import ImagePreview from '@/components/ui/images/ImagePreview';
import Button from '@/components/ui/button/Button';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { carReportsApi, ReportsListResponse } from '@/features/cars/api/carReportsApi';
import { CarModel } from '@/features/rentings/api/rentingsApi';

const ReportDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: report, isLoading, error } = useReport(id!);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditLoading, setIsEditLoading] = useState(false);

  const handlePrintClick = () => {
    window.print();
  };

  const handleEditClick = async () => {
    setIsEditLoading(true);
    try {
      // Предварительно загружаем технический профиль автомобиля
      await queryClient.fetchQuery({
        queryKey: ['car-tech-profile', report?.car.id],
        queryFn: () => carReportsApi.getTechProfile(report!.car.id)
      });

      // Предварительно загружаем данные отчета
      await queryClient.fetchQuery({
        queryKey: ['report', id],
        queryFn: () => carReportsApi.getReport(id!)
      });

      // Переходим на страницу редактирования
      if (report?.renting?.id) {
        navigate(`/rentings/${report.renting.id}/reports/${id}/edit`);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsEditLoading(false);
    }
  };

  if (isLoading) {
    return <div>{t('Loading...')}</div>;
  }

  if (error || !report) {
    return <div>{t('reports.details.error_loading')}</div>;
  }

  return (
    <>
      <PageMeta
        title={`${t('reports.details.title')} #${report.id} | Admin.Drivitive`}
        description={t('reports.details.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.rentings'), path: '/rentings' },
          { label: `${t('rentings.breadcrumb.renting')} #${report.renting.number}`, path: `/rentings/${report.renting.id}` },
          { label: `${report.activity === 'delivery_to_client' ? t('reports.details.delivery_report') : t('reports.details.pickup_report')} ${t('reports.details.for_renting')} #${report.renting.number}` }
        ]}
        actions={
          <Button
            variant="primary"
            size="md"
            onClick={handlePrintClick}
            className="print:hidden"
          >
            <svg width="20" height="20" viewBox="0 0 32 32" id="i-print" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
              <path d="M7 25 L2 25 2 9 30 9 30 25 25 25 M7 19 L7 30 25 30 25 19 Z M25 9 L25 2 7 2 7 9 M22 14 L25 14" />
            </svg>
            {t('reports.details.print')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 print:grid-cols-2 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5">
        {/* Report Info */}
        <div className="print:col-span-2 rounded-lg border border-stroke bg-white p-4 dark:border-gray-600 dark:bg-gray-800 md:p-6 xl:p-7.5">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold dark:text-white">{t('reports.details.report_details')}</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditClick}
              disabled={isEditLoading}
              className="print:hidden"
            >
              {isEditLoading ? (
                <span className="flex items-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></span>
                  {t('Loading...')}
                </span>
              ) : (
                t('reports.details.edit_report')
              )}
            </Button>
          </div>
          <div className="space-y-4">
            <div className="print:hidden">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.report_type')}</p>
              <p className="font-medium dark:text-white">
                {report.activity === 'delivery_to_client' ? t('reports.details.delivery_report') : t('reports.details.pickup_report')}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 print:grid-cols-2 print:pb-4">
              <div className="print:col-span-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.created_at')}</p>
                <p className="font-medium dark:text-white">
                  {format(new Date(report.created_at), 'PPp')}
                </p>
              </div>
              <div className="print:col-span-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.employee')}</p>
                <p className="font-medium dark:text-white">{report.employee.first_name} {report.employee.last_name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 print:grid-cols-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.mileage')}</p>
                <p className="font-medium dark:text-white">{report.mileage} km</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.fuel_level')}</p>
                <p className="font-medium dark:text-white">{report.fuel_level}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.charge_level')}</p>
                <p className="font-medium dark:text-white">{report.charge_level}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Renting Info */}
        <div className="print:col-span-1 border border-stroke bg-white p-4 dark:border-gray-600 dark:bg-gray-800 md:p-6 xl:p-7.5">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t('reports.details.renting_details')}</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.client')}</p>
              <Link
                to={`/clients/${report.renting.client.id}`}
                className="font-medium dark:text-white hover:underline"
              >
                {report.renting.client.first_name} {report.renting.client.last_name}
              </Link>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.details.period')}</p>
              <p className="font-medium dark:text-white">
                {format(new Date(report.renting.start_date), 'PP')} {report.renting.start_time}
                {' — '}
                {format(new Date(report.renting.end_date), 'PP')} {report.renting.end_time}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {report.activity === 'delivery_to_client' ? t('reports.details.delivery_address') : t('reports.details.pickup_address')}
              </p>
              <p className="font-medium dark:text-white">
                {report.activity === 'delivery_to_client'
                  ? report.renting.address_to_deliver
                  : report.renting.address_to_pickup}
              </p>
            </div>
          </div>
        </div>

        {/* Car Info */}
        <div className="print:col-span-1 rounded-lg border border-stroke bg-white p-4 dark:border-gray-600 dark:bg-gray-800 md:p-6 xl:p-7.5">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t('reports.details.car')}</h2>
          <div className="space-y-4">
            <div className="relative">
              <Link
                to={`/cars/${report.car.id}`}
                className="block relative print:max-w-[100px]"
                title={t('reports.details.view_car_details')}
              >
                <img
                  src={report.car.image_url}
                  alt={`${(report.car.model as unknown as CarModel).brand_name} ${(report.car.model as unknown as CarModel).model_name}`}
                  className="w-full rounded-lg object-cover flex-shrink-0 border border-gray-200 outline-[0px] transition-all duration-100 hover:outline hover:outline-[2px] outline-brand-400 dark:border-gray-800"
                />
              </Link>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium dark:text-white">
                {(report.car.model as unknown as CarModel).brand_name} {(report.car.model as unknown as CarModel).model_name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('reports.details.year')}: {report.car.release_year}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('reports.details.reg_number')}: {report.car.registration_number}
              </p>
            </div>
          </div>
        </div>

        {/* Report Details */}
        <div className="col-span-1 print:col-span-2 md:col-span-2 xl:col-span-3">
          <div className="rounded-lg border border-stroke bg-white p-4 dark:border-gray-600 dark:bg-gray-800 md:p-6 xl:p-7.5">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">{t('reports.details.car_details')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 print:grid-cols-2 gap-4">
              {report.details.map((detail, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg print:col-span-1"
                >
                  <h3 className="font-medium dark:text-white mb-3">{detail.detail}</h3>
                  <div className="space-y-4">
                    {detail.comments.map((comment, commentIndex) => (
                      <div key={commentIndex} className="space-y-2">
                        {comment.text && (
                          <p className="text-sm dark:text-gray-200">{comment.text}</p>
                        )}
                        {comment.file_url && (
                          <div className="mt-2 print:max-w-[100px]">
                            <ImagePreview imageUrl={comment.file_url}>
                              <img
                                src={comment.file_url}
                                alt={`${t('reports.details.detail')} ${detail.detail} ${t('reports.details.image')} ${commentIndex + 1}`}
                                className="rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary transition-colors"
                              />
                            </ImagePreview>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default ReportDetailsPage;
